import { otherSideOf } from "@/components/odds/history-market";
import type {
  OddsHistoryOpposite,
  OddsHistoryQuoteContext,
} from "@/components/odds/odds-history-selection";
import { getBookDisplayName } from "@/lib/utils/sports-books";
import type { BookOddsData } from "./comparison-table-types";
import type { DisplayPlayerProp } from "./utils-v2";

export function bestAtOwnLine(
  books: readonly BookOddsData[],
  odds: (book: BookOddsData) => number | null,
  lineOf: (book: BookOddsData) => number | null | undefined = (book) => book.line,
): (book: BookOddsData) => boolean {
  const top = new Map<number, number>();
  for (const book of books) {
    const price = odds(book);
    const line = lineOf(book);
    if (line != null && price != null) {
      top.set(line, Math.max(top.get(line) ?? price, price));
    }
  }
  return (book) => {
    const price = odds(book);
    const line = lineOf(book);
    return line != null && price != null && price === top.get(line);
  };
}

export function rowHistoryOpposite(
  row: DisplayPlayerProp,
  books: readonly BookOddsData[],
  anchor: string,
): OddsHistoryOpposite | undefined {
  const side = row.selection_side ?? row.bet_type ?? "";
  const otherOf = (book: BookOddsData | undefined) => otherSideOf(side, book?.line ?? row.line ?? null);
  const other = otherOf(undefined);
  if (!other) {
    return undefined;
  }
  const oppositeId = row.opp_selection_id || undefined;
  const sharesRowId = (book: BookOddsData) => book.selectionId != null && book.selectionId === row.selection_id;
  const anchorBook = books.find((book) => book.book === anchor);
  const own = oppositeId && anchorBook && sharesRowId(anchorBook) ? anchorBook : undefined;
  const oppositeLineOf = (book: BookOddsData | undefined) => otherOf(book)?.line ?? null;
  const altBest = bestAtOwnLine(books, (book) => book.oppositeOdds, oppositeLineOf);
  const quote = (book: BookOddsData): OddsHistoryQuoteContext => ({
    oddsAmerican: book.oppositeOdds ?? undefined,
    isBestPrice: book.isBestOpposite === true || altBest(book) ? true : undefined,
  });
  return {
    selectionId: own ? oppositeId : undefined,
    side: other.side,
    line: oppositeLineOf(anchorBook),
    context: own ? { quote: quote(own) } : undefined,
    peers: books.flatMap((book) =>
      book === own || book.oppositeOdds == null
        ? []
        : [
            {
              book: book.book,
              bookDisplayName: getBookDisplayName(book.book),
              ...(oppositeId && sharesRowId(book) ? { selectionId: oppositeId } : {}),
              line: oppositeLineOf(book),
              ...quote(book),
              oddsAmerican: book.oppositeOdds,
            },
          ],
    ),
  };
}
